import {useEffect, useRef, useState} from 'react';
import type {Dispatch, RefObject, SetStateAction} from 'react';

import {useAnimationControls} from 'motion/react';

export type PopoverState = {setOpen: Dispatch<SetStateAction<boolean>>};

/**
 * Hook for managing popover animation.
 *
 * This hook provides functionality for managing the animation of a popover component.
 * It controls the opening and closing animation of the popover based on the provided
 * `open` state. The hook returns the animation controls and popover ref to be used in
 * the popover component.
 *
 * @param state Ref object for managing the popover state.
 * @returns The animation controls and popover ref.
 *
 * @example
 * ```tsx
 * const {controls, popover} = usePopoverAnimation(state);
 * ```
 */
export const usePopoverAnimation = (state: RefObject<PopoverState>) => {
    const [open, setOpen] = useState(false);
    const [showContent, setShowContent] = useState(false);
    const popover = useRef<HTMLDialogElement>(null);
    const controls = useAnimationControls();

    // eslint-disable-next-line no-param-reassign
    state.current = {setOpen};

    useEffect(() => {
        const popoverDialog = popover.current;

        /**
         * Run the animation to show or hide the popover.
         *
         * This function triggers the animation for showing or hiding the popover
         * based on the current `open` state. If `open` is `true`, the popover is
         * displayed using the `showPopover` method and the enter animation is
         * started. If `open` is `false`, the exit animation is started and the
         * popover is closed.
         */
        const runAnimation = async () => {
            if (open) {
                setShowContent(true);
                popoverDialog?.show();
                popoverDialog?.style.setProperty('display', 'flex');
                await controls.start('enter');
            } else {
                await controls.start('exit');
                popoverDialog?.close();
                popoverDialog?.style.setProperty('display', 'none');
                setShowContent(false);
            }
        };

        void runAnimation();
    }, [controls, open]);

    return {
        controls,
        popover,
        setOpen,
        showContent
    };
};